import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({ name: '', subject: '', message: '' });
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent(formData.subject || 'İletişim Formu');
    const body = encodeURIComponent(`Ad Soyad: ${formData.name}\n\nMesaj:\n${formData.message}`);
    window.open(`mailto:info@sbtkimya.com.tr?subject=${subject}&body=${body}`, '_blank');
    setSent(true);
    setTimeout(() => setSent(false), 3000);
    setFormData({ name: '', subject: '', message: '' });
  };

  const mapUrl = `https://www.google.com/maps/search/?api=1&query=39.8379,32.9355+Yeni+Birlik+Sanayi+Sitesi+Saray+675+Cadde+No+24+12+Kahramankazan+Ankara`;
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=39.8379,32.9355+T-RaX+Car+Care+Kahramankazan+Ankara`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a2e] text-white py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl font-black">📍 {t('contact.title')}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-6">📞 {t('contact.info')}</h2>

              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#E31E24]/10 rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-lg">📍</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{t('contact.address')}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">
                      Yeni Birlik Sanayi Sitesi, Saray, 675. Cadde No:24/12,<br />
                      06980 Kahramankazan/Ankara
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#E31E24]/10 rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-lg">📞</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{t('contact.phone')}</h3>
                    <a href="tel:+903128151165" className="text-sm text-[#E31E24] font-semibold hover:underline">
                      0312 815 11 65
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#E31E24]/10 rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-lg">✉️</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{t('contact.email')}</h3>
                    <a href="mailto:info@sbtkimya.com.tr" className="text-sm text-[#E31E24] font-semibold hover:underline">
                      info@sbtkimya.com.tr
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#E31E24]/10 rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-lg">🕐</span>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-800 mb-1">{t('contact.hours')}</h3>
                    <div className="text-sm text-gray-500 space-y-1">
                      <p><span className="font-medium">{t('contact.weekdays')}:</span> 08:00 - 17:30</p>
                      <p><span className="font-medium">{t('contact.weekends')}:</span> 08:30 - 14:30</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Button */}
              <a
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#E31E24]/30 transition-all uppercase tracking-wider text-sm"
              >
                🗺️ {t('contact.navigate')}
              </a>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 sm:p-8">
              <h2 className="text-xl font-bold text-gray-800 mb-6">💬 {t('contact.sendMessage')}</h2>

              {sent ? (
                <div className="text-center py-8">
                  <span className="text-4xl mb-4 block">✅</span>
                  <p className="text-gray-600 font-semibold">Mesajınız gönderildi!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5">{t('contact.name')}</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5">{t('contact.subject')}</label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1.5">{t('contact.message')}</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm outline-none focus:border-[#E31E24] transition-colors resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-3 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-lg hover:shadow-[#E31E24]/30 transition-all uppercase tracking-wider text-sm"
                  >
                    📨 {t('contact.send')}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Map */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h3 className="text-sm font-bold text-gray-800">📍 {t('contact.address')}</h3>
              </div>
              <div className="aspect-video w-full">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3064.5!2d32.9355!3d39.8379!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDUwJzE2LjQiTiAzMsKwNTYnMDcuOCJF!5e0!3m2!1str!2str!4v1700000000000!5m2!1str!2str`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="T-RaX Car Care Konum"
                />
              </div>
              <div className="p-4">
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center py-2.5 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors text-sm"
                >
                  📍 Google Maps'te Aç
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
